/**
 * Guided tutorial — the pure pieces: a hand-built teaching battle and the
 * scripted step list. Ported from the legacy v2 build
 * (legacy/admirals-return-godot/scripts/main.gd start_tutorial / TUT_STEPS).
 *
 * The map is a fixed 14×9 sea with three cities (the player's home Norfolk, the
 * neutral prize Charleston, the enemy's Boston) and a small starting fleet for
 * each side, so the lesson always plays out the same way. The step list drives
 * an overlay panel (ui/tutorial.ts): each step either waits for the player to
 * do something (`when` = an event the game emits) or shows a "Next" button
 * (`when: 'next'`); the final step (`when: 'done'`) dismisses the panel.
 *
 * Pure: no UI/DOM. The step text carries inline <b> markup the panel renders.
 */
import { markCityTiles } from './deploy';
import { addShip } from './ships';
import { newGameState, type GameState } from './state';

/** What the game must report for a `when`-gated step to advance. */
export type TutorialTrigger =
  | 'next' // player clicks "Next"
  | 'done' // final step; player clicks "let me play"
  | 'select:TR' // player selects their Transport
  | 'move' // player moves a ship
  | 'endmove' // player ends their movement (Fin / End Turn)
  | 'newturn'; // a new game turn begins

export interface TutorialStep {
  title: string;
  /** body text, may contain <b>…</b> */
  text: string;
  when: TutorialTrigger;
}

/** The ~10-step script (legacy TUT_STEPS, BBCode → HTML). */
export const TUT_STEPS: TutorialStep[] = [
  {
    title: 'Welcome aboard, Admiral!',
    text: 'This is a battle for <b>points</b> ({VP}). Cities pay their {VP} value every turn you keep a Transport in an <b>adjacent</b> tile. Outscore the enemy before the turns run out.',
    when: 'next',
  },
  {
    title: 'Select your Transport',
    text: 'Click your <b>Transport</b> beside your home port of Norfolk — the low grey cargo ship stacked with deck containers. It carries no guns, but it\'s the only ship that can claim a city.',
    when: 'select:TR',
  },
  {
    title: 'Move it',
    text: 'Red buoys mark where it can sail. Click one to move. Head <b>south to sit next to Charleston (300)</b> — a neutral city worth {VP}300 each turn once you hold it.',
    when: 'move',
  },
  {
    title: 'Escort it',
    text: "Transports die in one shot, but they're <b>protected</b> while a friendly warship sits in an <b>adjacent</b> tile. Move your <b>Destroyer</b> alongside.",
    when: 'move',
  },
  {
    title: 'End your movement',
    text: 'Press <b>End Turn</b> when you\'re done moving. Combat fires wherever your ships sit <b>next to</b> the enemy; then the enemy moves.',
    when: 'endmove',
  },
  {
    title: 'Cities pay every turn',
    text: "Each full turn, every city with one of your Transports beside it pays out. Keep your Transport <b>next to</b> Charleston — the flag turns red when it's yours (an enemy ship alongside blocks the capture until you sink it).",
    when: 'newturn',
  },
  {
    title: 'Rock, paper, scissors',
    text: 'Battleships crush Destroyers. Destroyers one-shot submerged Subs. Submerged Subs maul Battleships without taking a scratch.',
    when: 'next',
  },
  {
    title: 'Your submarine',
    text: 'Submerged subs are invisible except to sub-hunters (DD/PT/CR) and move 2. <b>Surface</b> (U key) to move 3 — but surfaced subs are easy prey.',
    when: 'next',
  },
  {
    title: 'Building ships',
    text: '{VP}200 cities you own are shipyards — spend your points there to build new ships instantly, any time, from the <b>Build</b> tab.',
    when: 'next',
  },
  {
    title: 'Win the battle!',
    text: 'Hold Norfolk, take Charleston, and outscore the enemy by the win ratio in the top bar. Good hunting, Admiral!',
    when: 'done',
  },
];

/**
 * Build the fixed tutorial battle state. A 14×9 sea with a few land tiles,
 * three cities, and the scripted opening fleets for both sides. Deterministic
 * (a fixed seed seeds combat RNG); the win ratio is an easy 10%.
 */
export function buildTutorialState(seed = 0xada): GameState {
  const g = newGameState({
    size: 'small',
    seed,
    maxTurn: 10,
    budgetR: 0,
    budgetY: 0,
    forceRatio: 10,
    forceFavored: 'R',
  });

  // Hand-built 14×9 sea (overrides the generated map).
  g.W = 14;
  g.H = 9;
  g.nCities = 3;
  g.tiles = Array.from({ length: 9 }, () => Array.from({ length: 14 }, () => 'water' as const));
  // land tiles as [c, r] (legacy lr pairs)
  const land: [number, number][] = [
    [6, 2], [6, 3], [6, 4], [6, 5], [6, 6], [7, 3], [7, 5], [2, 6], [3, 6], [10, 2], [11, 2],
  ];
  for (const [c, r] of land) g.tiles[r]![c] = 'land';

  g.cities = [
    { c: 5, r: 3, name: 'Norfolk', vp: 200, owner: 'R', home: 'R' },
    { c: 5, r: 5, name: 'Charleston', vp: 300, owner: null, home: null },
    { c: 10, r: 3, name: 'Boston', vp: 200, owner: 'Y', home: 'Y' },
  ];
  markCityTiles(g); // cities are impassable land — ships sit on the water beside them

  g.ships = [];
  g.nextId = 1;
  // one ship per tile (house rule): the fleet starts spread on water NEXT TO the
  // home ports (cities are land now). The Red BB sits beside Norfolk so it holds
  // the home port even after the lone Transport sails south to take Charleston.
  addShip(g, 'R', 'TR', 5, 2); // on the water just north of Norfolk (5,3)
  addShip(g, 'R', 'DD', 4, 2); // escort beside the transport
  addShip(g, 'R', 'BB', 4, 3); // beside Norfolk — keeps the home port held
  addShip(g, 'R', 'SS', 4, 4);
  addShip(g, 'Y', 'TR', 10, 4); // beside Boston (10,3)
  addShip(g, 'Y', 'DD', 11, 4);
  addShip(g, 'Y', 'PT', 9, 4);

  g.phase = 'move';
  return g;
}
